import { useTranslation } from "react-i18next";
import { cx } from "@/components/ui";
import type { LesenGuidedPassageContent } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

type GuidedAnswer = { gaps?: Record<string, string>; stmts?: string[] };

/** Two-card guided reading: fill the passage gaps, then mark which statements
 *  the text discussed (mirrors iOS LesenGuidedPassageView; whole-item grade). */
export function LesenGuidedPassageRenderer({ content, answer, setAnswer, revealed }: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as LesenGuidedPassageContent;
  const a = (answer as GuidedAnswer) ?? {};
  const gaps = a.gaps ?? {};
  const picked = new Set(a.stmts ?? []);

  const setGap = (idx: number, label: string) =>
    setAnswer({ ...a, gaps: { ...gaps, [String(idx)]: label } });
  const toggleStmt = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setAnswer({ ...a, stmts: [...next] });
  };

  return (
    <div>
      <Prompt>{c.prompt}</Prompt>
      {c.passage?.text && (
        <div className="mt-3 whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-sm text-ink">{c.passage.text}</div>
      )}

      {/* Card 1 — gaps */}
      <div className="mt-4 space-y-3">
        {(c.passage?.gaps ?? []).map((g) => {
          const chosen = gaps[String(g.index)];
          return (
            <div key={g.index} className="rounded-xl border border-black/10 p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                {t("Gap {{n}}", { n: g.index + 1 })}{g.kind ? ` · ${g.kind}` : ""}
              </div>
              <div className="flex flex-wrap gap-2">
                {g.candidates.map((cand) => {
                  let tone = "border-black/10 bg-white hover:border-brand-300";
                  if (revealed) {
                    if (cand.label === g.correct_label) tone = "border-emerald-400 bg-emerald-50";
                    else if (cand.label === chosen) tone = "border-red-400 bg-red-50";
                    else tone = "border-black/10 bg-white opacity-60";
                  } else if (cand.label === chosen) tone = "border-brand-500 bg-brand-50 ring-1 ring-brand-300";
                  return (
                    <button
                      key={cand.label}
                      type="button"
                      disabled={revealed}
                      onClick={() => setGap(g.index, cand.label)}
                      className={cx("rounded-lg border px-3 py-1.5 text-sm transition disabled:cursor-default", tone)}
                    >
                      {cand.text}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Card 2 — comprehension (multi-select "discussed") */}
      {(c.comprehension?.statements ?? []).length > 0 && (
        <div className="mt-5">
          <div className="mb-2 text-sm font-semibold text-ink">{t("Which did the text discuss?")}</div>
          <div className="space-y-2">
            {(c.comprehension?.statements ?? []).map((s) => {
              const isPicked = picked.has(s.id);
              let tone = isPicked ? "border-brand-500 bg-brand-50" : "border-black/10 bg-white hover:border-brand-300";
              if (revealed) {
                const ok = (s.verdict === "discussed") === isPicked;
                tone = ok ? "border-emerald-400 bg-emerald-50" : "border-red-400 bg-red-50";
              }
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={revealed}
                  onClick={() => toggleStmt(s.id)}
                  className={`flex w-full items-start gap-2 rounded-xl border px-4 py-3 text-left text-sm transition disabled:cursor-default ${tone}`}
                >
                  <span className="mt-0.5">{isPicked ? "☑" : "☐"}</span>
                  <span>{s.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
